import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useReferralSummaryQuery } from '../hooks/useReferralSummaryQuery';
import { useSessionStore, selectIsAuthenticated } from '../../style-transfer/state/sessionStore';
import { showToast } from '../../../shared/notifications/toastStore';
import type { ReferralRewardRecord, ReferralSummaryResponse } from '../../../shared/api/contracts/referralSchemas';

const DEFAULT_REFERRAL_REWARD = 20;

export function InviteSummaryBanner() {
  const { t } = useTranslation();
  const isAuthenticated = useSessionStore(selectIsAuthenticated);
  const { data, isLoading, isFetching, error, refetch } = useReferralSummaryQuery(isAuthenticated);
  const [hasCopied, setHasCopied] = useState(false);

  const summary = data ?? null;
  const rewardAmount = summary?.rewardAmount ?? DEFAULT_REFERRAL_REWARD;
  const totalRewardPoints = summary?.totalRewardAmount ?? 0;
  const shareUrl = useMemo(() => resolveShareUrl(summary), [summary]);

  if (!isAuthenticated) {
    return null;
  }

  const copyLink = async (link: string) => {
    await navigator.clipboard.writeText(link);
    setHasCopied(true);
    showToast({ message: t('memberPage.invite.copied'), tone: 'info' });
    window.setTimeout(() => setHasCopied(false), 2500);
  };

  const handleCopy = async () => {
    if (!shareUrl) {
      showToast({ message: t('memberPage.invite.generating'), tone: 'info' });
      try {
        const result = await refetch({ cancelRefetch: false });
        const refreshedUrl = resolveShareUrl(result.data ?? null);
        if (refreshedUrl) {
          await copyLink(refreshedUrl);
        } else {
          showToast({ message: t('memberPage.invite.linkUnavailable'), tone: 'warning' });
        }
      } catch {
        showToast({ message: t('memberPage.invite.copyError'), tone: 'danger' });
      }
      return;
    }
    try {
      await copyLink(shareUrl);
    } catch {
      showToast({ message: t('memberPage.invite.copyError'), tone: 'danger' });
    }
  };

  const renderRecent = () => {
    if (!summary?.recentRewards?.length) {
      return (
        <p className="text-sm text-ai-text-secondary">
          {t('memberPage.invite.recentEmpty')}
        </p>
      );
    }
    return (
      <ul className="space-y-2">
        {summary.recentRewards.slice(0, 3).map((reward) => (
          <li
            key={`${reward.status}-${reward.inviteeEmail}-${reward.createdAt ?? ''}`}
            className="flex items-center justify-between rounded-2xl border border-ai-surface-glow/30 bg-ai-surface-elevated/70 px-3 py-2 text-sm"
          >
            <span className="font-semibold text-ai-text-primary">
              {reward.inviteeEmail ?? t('memberPage.invite.anonymous')}
            </span>
            <span className={clsx('text-xs font-semibold uppercase tracking-[0.3em]', statusTone(reward))}>
              {t(`memberPage.invite.status.${reward.status}`)}
            </span>
          </li>
        ))}
      </ul>
    );
  };

  const stats = [
    {
      label: t('memberPage.invite.totalEarned'),
      value: totalRewardPoints
    }
  ];

  return (
    <section className="mb-6 rounded-3xl border border-ai-surface-glow/40 bg-ai-surface-elevated/80 p-5 shadow-ai-card sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.35em] text-ai-text-secondary">
            {t('memberPage.invite.label')}
          </p>
          <h2 className="mt-1 font-display text-2xl font-semibold text-ai-text-primary sm:text-3xl">
            {t('memberPage.invite.title')}
          </h2>
          <p className="mt-1 text-sm text-ai-text-secondary sm:text-base">
            {t('memberPage.invite.subtitle', { reward: rewardAmount })}
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:items-end">
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-full border border-ai-primary/60 px-4 py-2 text-xs font-semibold uppercase tracking-[0.35em] text-ai-text-primary transition-colors hover:border-ai-primary focus:outline-none focus-visible:ring focus-visible:ring-ai-focus disabled:cursor-not-allowed disabled:opacity-60 sm:text-sm"
            onClick={handleCopy}
            disabled={isFetching}
          >
            {hasCopied ? t('memberPage.invite.copied') : t('memberPage.invite.copy')}
          </button>
        </div>
      </div>
      {error ? (
        <div className="mt-4 flex items-center justify-between rounded-2xl border border-ai-warning/40 bg-ai-warning/10 px-4 py-3 text-sm text-ai-warning">
          <span>{t('memberPage.invite.error')}</span>
          <button
            type="button"
            className="text-xs font-semibold uppercase tracking-[0.35em] hover:text-ai-text-primary focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
            onClick={() => refetch()}
          >
            {t('memberPage.invite.refresh')}
          </button>
        </div>
      ) : null}
      <div className="mt-4 grid gap-3 sm:grid-cols-1">
        {stats.map((stat) => (
          <div
            key={stat.label}
            className="rounded-2xl border border-ai-surface-glow/30 bg-ai-surface/60 px-4 py-3 shadow-inner"
          >
            <p className="text-xs uppercase tracking-[0.3em] text-ai-text-secondary">{stat.label}</p>
            <p className="mt-1 text-2xl font-semibold text-ai-text-primary">{stat.value}</p>
          </div>
        ))}
      </div>
      <div className="mt-4 rounded-2xl border border-ai-surface-glow/30 bg-ai-surface/60 p-4">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-ai-text-secondary">
            {t('memberPage.invite.recentHeading')}
          </p>
          {isLoading || isFetching ? (
            <span className="text-xs text-ai-text-secondary">{t('memberPage.invite.loading')}</span>
          ) : null}
        </div>
        <div className="mt-3">{renderRecent()}</div>
      </div>
    </section>
  );
}

function resolveShareUrl(summary: ReferralSummaryResponse | null): string | null {
  if (!summary) {
    return null;
  }

  const rawShare = summary.shareUrl ?? (summary.referralCode ? `/?ref=${summary.referralCode}` : null);
  if (!rawShare) {
    return null;
  }

  if (typeof window !== 'undefined') {
    try {
      const resolved = new URL(rawShare, window.location.origin);
      return resolved.toString();
    } catch {
      return `${window.location.origin}/?ref=${encodeURIComponent(summary.referralCode ?? '')}`;
    }
  }

  return rawShare;
}

function statusTone(reward: ReferralRewardRecord) {
  switch (reward.status) {
    case 'COMPLETED':
      return 'text-ai-primary';
    case 'PENDING':
      return 'text-ai-text-secondary';
    default:
      return 'text-ai-warning';
  }
}
